import { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import {
  authenticateUser,
  completeNewPasswordChallenge,
  forgotPassword,
  confirmPassword
} from '../auth/cognitoAuth';
import { logger } from '../utils/logger';
import './Login.css';

interface UserInfo {
  id: string;
  email: string;
  isAdmin: boolean;
  permissions: string[];
  useCases: string[];
}

const Login: React.FC<{ onLogin: (user: UserInfo) => void }> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [confirmResetPassword, setConfirmResetPassword] = useState('');
  
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [requiresNewPassword, setRequiresNewPassword] = useState(false);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [resetCodeSent, setResetCodeSent] = useState(false);
  const [oldPassword, setOldPassword] = useState('');

  // Extract user info from ID token
  const extractUserInfoFromToken = (idToken: string): UserInfo => {
    const payload = JSON.parse(atob(idToken.split('.')[1]));
    
    const permissions = payload['custom:permissions'] 
      ? payload['custom:permissions'].split(',').map((p: string) => p.trim()).filter(Boolean)
      : [];
    
    const useCases = payload['custom:useCases']
      ? payload['custom:useCases'].split(',').map((u: string) => u.trim()).filter(Boolean)
      : ['logistics'];
    
    return {
      id: payload.sub,
      email: payload.email,
      isAdmin: payload['custom:isAdmin'] === 'true',
      permissions,
      useCases
    };
  };

  // Handle initial login
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const result = await authenticateUser(email, password);

      if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setRequiresNewPassword(true);
        setOldPassword(password);
        setLoading(false);
        return;
      }

      // Extract user info from ID token
      const userInfo = extractUserInfoFromToken(result.tokens.idToken);
      
      logger.info('User logged in via Cognito:', userInfo.email);
      onLogin(userInfo);
    } catch (err: any) {
      const message = err.message || 'Login failed';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Handle new password submission
  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const result = await completeNewPasswordChallenge(email, oldPassword, newPassword);
      
      // Extract user info from ID token
      const userInfo = extractUserInfoFromToken(result.tokens.idToken);
      
      logger.info('User completed password change and logged in:', userInfo.email);
      onLogin(userInfo);
    } catch (err: any) {
      const message = err.message || 'Password change failed';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Handle forgot password - send code
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await forgotPassword(email);
      setResetCodeSent(true);
      setError(null);
    } catch (err: any) {
      const message = err.message || 'Failed to send reset code';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Handle password reset with code
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (resetPassword !== confirmResetPassword) {
      setError('Passwords do not match');
      return;
    }

    if (resetPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await confirmPassword(email, verificationCode, resetPassword);
      
      // Reset state and return to login
      setForgotPasswordMode(false);
      setResetCodeSent(false);
      setVerificationCode('');
      setResetPassword('');
      setConfirmResetPassword('');
      setError(null);
      
      // Show success message
      alert('Password reset successful! Please log in with your new password.');
    } catch (err: any) {
      const message = err.message || 'Password reset failed';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Show new password form if required
  if (requiresNewPassword) {
    return (
      <div className="login-page">
        <main className="login-main">
          <div className="column-section left-section">
            <div className="logo">
              <img 
                src="/images/landing/logo/zeefleet-v2-logo-negative.png" 
                alt="ZEEFLEET Logo" 
              />
            </div>
          </div>

          <div className="column-section right-section">
            <div className="login-form-container">
              <h1 className="login-header">Set New Password</h1>
              <p className="subtitle">Your administrator requires you to change your password</p>

              {error && (
                <div className="login-message error">
                  <AlertCircle className="login-message-icon" />
                  <p>{error}</p>
                </div>
              )}

              <form onSubmit={handleNewPassword} className="login-form-inputs">
                <div className="input-container">
                  <label htmlFor="newPassword">New Password</label>
                  <input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    placeholder="Enter new password (min 8 characters)"
                  />
                </div>

                <div className="input-container">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                    minLength={8}
                    placeholder="Confirm new password"
                  />
                </div>

                <button 
                  type="submit" 
                  className="login-button"
                  disabled={loading}
                >
                  {loading ? 'Updating...' : 'Set Password'}
                </button>
              </form>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show forgot password flow
  if (forgotPasswordMode) {
    if (resetCodeSent) {
      return (
        <div className="login-page">
          <main className="login-main">
            <div className="column-section left-section">
              <div className="logo">
                <img 
                  src="/images/landing/logo/zeefleet-v2-logo-negative.png" 
                  alt="ZEEFLEET Logo" 
                />
              </div>
            </div>

            <div className="column-section right-section">
              <div className="login-form-container">
                <h1 className="login-header">Reset Password</h1>
                <p className="subtitle">Enter the verification code sent to {email}</p>

                {error && (
                  <div className="login-message error">
                    <AlertCircle className="login-message-icon" />
                    <p>{error}</p>
                  </div>
                )}

                <form onSubmit={handleResetPassword} className="login-form-inputs">
                  <div className="input-container">
                    <label htmlFor="verificationCode">Verification Code</label>
                    <input
                      id="verificationCode"
                      type="text"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      required
                      placeholder="Enter 6-digit code"
                    />
                  </div>

                  <div className="input-container">
                    <label htmlFor="resetPassword">New Password</label>
                    <input
                      id="resetPassword"
                      type="password"
                      value={resetPassword}
                      onChange={(e) => setResetPassword(e.target.value)}
                      required
                      minLength={8}
                      placeholder="Enter new password (min 8 characters)"
                    />
                  </div>

                  <div className="input-container">
                    <label htmlFor="confirmResetPassword">Confirm Password</label>
                    <input
                      id="confirmResetPassword"
                      type="password"
                      value={confirmResetPassword}
                      onChange={(e) => setConfirmResetPassword(e.target.value)}
                      required
                      minLength={8}
                      placeholder="Confirm new password"
                    />
                  </div>

                  <button 
                    type="submit" 
                    className="login-button"
                    disabled={loading}
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </button>

                  <button
                    type="button"
                    className="forgot-password-link"
                    onClick={() => {
                      setForgotPasswordMode(false);
                      setResetCodeSent(false);
                    }}
                  >
                    Back to Login
                  </button>
                </form>
              </div>
            </div>
          </main>
        </div>
      );
    }

    return (
      <div className="login-page">
        <main className="login-main">
          <div className="column-section left-section">
            <div className="logo">
              <img 
                src="/images/landing/logo/zeefleet-v2-logo-negative.png" 
                alt="ZEEFLEET Logo" 
              />
            </div>
          </div>

          <div className="column-section right-section">
            <div className="login-form-container">
              <h1 className="login-header">Forgot Password</h1>
              <p className="subtitle">Enter your email to receive a password reset code</p>

              {error && (
                <div className="login-message error">
                  <AlertCircle className="login-message-icon" />
                  <p>{error}</p>
                </div>
              )}

              <form onSubmit={handleForgotPassword} className="login-form-inputs">
                <div className="input-container">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="john@doe.com"
                  />
                </div>

                <button 
                  type="submit" 
                  className="login-button"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Reset Code'}
                </button>

                <button
                  type="button"
                  className="forgot-password-link"
                  onClick={() => setForgotPasswordMode(false)}
                >
                  Back to Login
                </button>
              </form>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Standard login form

  // Standard login form
  return (
    <div className="login-page">
      <main className="login-main">
        <div className="column-section left-section">
          <div className="logo">
            <img 
              src="/images/landing/logo/zeefleet-v2-logo-negative.png" 
              alt="ZEEFLEET Logo" 
            />
          </div>
        </div>

        <div className="column-section right-section">
          <div className="login-form-container">
            <h1 className="login-header">Sign In</h1>
            
            {error && (
              <div className="login-message error">
                <AlertCircle className="login-message-icon" />
                <p>{error}</p>
              </div>
            )}

            <form onSubmit={handleLogin} className="login-form-inputs">
              <div className="input-container">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="john@doe.com"
                  autoComplete="email"
                />
              </div>

              <div className="input-container">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
              </div>

              <button
                type="button"
                className="forgot-password-link"
                onClick={() => setForgotPasswordMode(true)}
              >
                Forgot Password?
              </button>

              <button 
                type="submit" 
                className="login-button button-primary"
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;
